import { useState, useEffect, useMemo } from 'react';
import queryString from 'query-string';
import { useLocation } from 'react-router';
import {
    amrPipelineCommonActions,
} from '../../../actions/';
import { Pipeline } from './Pipeline';
import { PipelineDashboard } from './PipelineDashboard';
import { PipelineView } from '../types/PipelineView';
import { useSelector } from 'react-redux';
import PipelineContext from './PipelineContext';
import { PipelineType } from '../../../types/amr-pipeline/enums/PipelineType';
import { ArrangerPipeline } from './arranger-pipeline/ArrangerPipeline';
import { IssuanceMonitorTab } from '../types/PipelineTabTypes';
import { ArrangerPipelineEdit } from './arranger-pipeline/ArrangerPipelineEdit';
import { user } from '../../../user';
import { roles } from '../../../constants';
import { SubscriptionFeature } from '../../../types/billing/SubscriptionFeature';
import { amrDealsSelector, arrangerPipelineDealsSelector } from '../../../selectors/amr-pipeline.selector';
import { IssuanceMonitorLayout } from './IssuanceMonitorLayout';
import { useAppDispatch } from '../../../effects/useAppDispatch';
import { ImAccessType } from '../../../types/amr-pipeline/enums/ImAccessType';

const viewLogEvents = {
    [IssuanceMonitorTab.Deals]: {
        [PipelineView.Deal]: ImAccessType.imDealsDealView,
        [PipelineView.Class]: ImAccessType.imDealsClassView,
        [PipelineView.KMatrix]: ImAccessType.imDealsKMatrix,
    },
    [IssuanceMonitorTab.IOIs]: {
        [PipelineView.Deal]: ImAccessType.imIoisDealView,
        [PipelineView.Class]: ImAccessType.imIoisClassView,
        [PipelineView.KMatrix]: null,
    },
    [IssuanceMonitorTab.Dashboard]: null,
    [IssuanceMonitorTab.ArrangerPipeline]: null,
    [IssuanceMonitorTab.ArrangerPipelineEdit]: null,
}

const tabLogEvents = {
    [IssuanceMonitorTab.Deals]: null,
    [IssuanceMonitorTab.Dashboard]: ImAccessType.imDashboard,
    [IssuanceMonitorTab.IOIs]: null,
    [IssuanceMonitorTab.ArrangerPipeline]: ImAccessType.imArrangerPipeline,
    [IssuanceMonitorTab.ArrangerPipelineEdit]: null,
}

export const IssuanceMonitor = () => {
    const dispatch = useAppDispatch();
    const location = useLocation();

    const { imPipelineTab, pipelineViewMode } = queryString.parse(location.search, { decode: false });

    const anyPipelineExists = useSelector(arrangerPipelineDealsSelector).anyPipelineExists;
    const anyIOIsExists = useSelector(amrDealsSelector(PipelineType.IOIs)).anyIOIsExists;

    const withImSubscription = user.hasFeatures(SubscriptionFeature.IssuanceMonitorFullAccess);

    const [viewMode, setViewMode] = useState(pipelineViewMode as PipelineView || PipelineView.Deal);
    const [tab, setTab] = useState(imPipelineTab as IssuanceMonitorTab || IssuanceMonitorTab.Deals);

    const [isOptionsColumnCollapsed, setIsOptionsColumnCollapsed] = useState({
        [IssuanceMonitorTab.Deals]: true,
        [IssuanceMonitorTab.IOIs]: true
    });

    const [filtersCollapseStatus, setFiltersCollapseStatus] = useState({
        [PipelineType.Deals]: false,
        [PipelineType.IOIs]: false,
        [PipelineType.ArrangerPipeline]: false
    });

    const contextValue = useMemo(() => {
        const collapseState = {
            filtersCollapseStatus,
            setFiltersCollapseStatus: (isCollapsed: boolean) => {
                setFiltersCollapseStatus(prevValue => ({
                    ...prevValue,
                    [tab]: isCollapsed,
                }));
            },
        };

        const optionCollapseStatus = {
            optionColumnCollapseStatus: isOptionsColumnCollapsed,
            setOptionColumnCollapseStatus: (isCollapsed: boolean) => {
                setIsOptionsColumnCollapsed(prevValue => ({
                    ...prevValue,
                    [tab]: isCollapsed
                }))
            }
        };

        const tabsState = {
            tab,
            setTab: (tab: IssuanceMonitorTab) => setTab(tab),
        }

        switch (tab) {
            case IssuanceMonitorTab.IOIs:
                return {
                    pipelineType: PipelineType.IOIs,
                    ...tabsState,
                    ...collapseState,
                    ...optionCollapseStatus,
                    withImSubscription,
                    withBlockedSearchAndFilter: !anyIOIsExists,
                };
            case IssuanceMonitorTab.ArrangerPipeline:
            case IssuanceMonitorTab.ArrangerPipelineEdit:
                return {
                    pipelineType: PipelineType.ArrangerPipeline,
                    ...tabsState,
                    ...collapseState,
                    ...optionCollapseStatus,
                    withImSubscription,
                    withBlockedSearchAndFilter: !anyPipelineExists,
                };
            default:
                return {
                    pipelineType: PipelineType.Deals,
                    ...tabsState,
                    ...collapseState,
                    ...optionCollapseStatus,
                    withImSubscription,
                };
        }
    }, [tab, filtersCollapseStatus, isOptionsColumnCollapsed, withImSubscription, anyPipelineExists, anyIOIsExists]);

    useEffect(() => {
        if (user.hasSingleRole(roles.ArrangersClient)) {
            dispatch(amrPipelineCommonActions.redirectInvitedClientToTransactionDetails())
            return;
        }
    }, [dispatch]);

    useEffect(() => {
        const viewLogs = viewLogEvents[tab];
        if(viewLogs) {
            const logEvent = viewLogs[viewMode];

            if(logEvent) {
                dispatch(amrPipelineCommonActions.logImUserActivity(logEvent));
            }
        }
    }, [viewMode, tab, dispatch]);

    useEffect(() => {
        const logEvent = tabLogEvents[tab];

        if(logEvent) {
            dispatch(amrPipelineCommonActions.logImUserActivity(logEvent));
        }
    }, [tab, dispatch]);

    const renderPipeline = (tab: IssuanceMonitorTab) => {
        switch (tab) {
            case IssuanceMonitorTab.Dashboard:
                return <PipelineDashboard onWidgetItemClick={() => setTab(IssuanceMonitorTab.Deals)} />;
            case IssuanceMonitorTab.ArrangerPipeline:
                return <ArrangerPipeline />;
            case IssuanceMonitorTab.Deals:
            case IssuanceMonitorTab.IOIs:
                return <Pipeline viewMode={viewMode} onRedirect={() => setTab(IssuanceMonitorTab.Deals)} onViewModeChange={setViewMode} />;
            default:
                return null;
        }
    };

    const renderIssuanceMonitor = () => {
        if (tab === IssuanceMonitorTab.ArrangerPipelineEdit) {
            return <ArrangerPipelineEdit />;
        }

        const handleTabChange = (newTab: IssuanceMonitorTab) => {
            if (newTab === IssuanceMonitorTab.IOIs && viewMode === PipelineView.KMatrix) {
                setViewMode(PipelineView.Deal);
            }

            setTab(newTab);
        };

        return (
            <IssuanceMonitorLayout tab={tab} setTab={handleTabChange} viewMode={viewMode}>
                {renderPipeline(tab)}
            </IssuanceMonitorLayout>
        )
    };

    return (
        <PipelineContext.Provider value={contextValue}>
            <div className="container container-pipeline">
                {renderIssuanceMonitor()}
            </div>
        </PipelineContext.Provider>
    );
};
